import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Leaf, ArrowRight, Puzzle, Droplets, FlaskConical, Calendar, Users } from 'lucide-react';
import { Card } from '../Card';
import { cn } from '../../../utils/cn';
import { useReducedMotion } from '../../../hooks/useReducedMotion';
import {
  DIFFICULTY_LABELS,
  WATER_LABELS,
  NUTRIENT_LABELS,
  translateCategory,
  getDifficultyLabel
} from '../../../pages/PlantOPedia/utils/plantopedia-utils';
import type { SpeciesCardProps } from './SpeciesCard.types';
import { DotRating, getDifficultyColor } from './DotRating';

// --- Sowing time formatter ---
const toRoman = (num: number): string => {
  const roman = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];
  return roman[num - 1] || '';
};

const formatSowingTime = (period?: string | string[]): string => {
  if (!period) return '';

  const periods = Array.isArray(period) ? period : [period];
  const czMonths = [
    'leden', 'únor', 'březen', 'duben', 'květen', 'červen',
    'červenec', 'srpen', 'září', 'říjen', 'listopad', 'prosinec',
  ];

  const formatted = periods.map(p => {
    let s = p.toLowerCase();
    for (let i = 0; i < czMonths.length; i++) {
      const regex = new RegExp(czMonths[i], 'gi');
      s = s.replace(regex, toRoman(i + 1));
    }
    return s.replace(/\s*-\s*/g, '–').toUpperCase();
  });

  return formatted.join(', ');
};

export const SpeciesCard: React.FC<SpeciesCardProps> = ({ species, onClick, className }) => {
  const navigate = useNavigate();
  const reducedMotion = useReducedMotion();

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else {
      navigate(`/plant-o-pedia/species/${species.id}`);
    }
  };

  const getVal = (key: string) =>
    (species as any)[key] || (species as any).growth_characteristics?.[key];

  const sowingIndoor = getVal('sowing_indoor');
  const sowingOutdoor = getVal('sowing_outdoor');
  const sowingGeneral = getVal('sowing_period');
  const hasSowing = sowingIndoor || sowingOutdoor || sowingGeneral;

  const difficulty = species.difficulty_level || (species as any).difficulty || 3;
  const waterNeed =
    species.water_need_level || (species as any).optimal_environment?.water_need_level;
  const nutrientDemand =
    species.nutrient_demand || (species as any).optimal_environment?.nutrient_demand;

  return (
    <motion.div
      initial={reducedMotion ? false : { opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      whileHover={reducedMotion ? {} : { y: -4 }}
      onClick={handleClick}
      role="link"
      tabIndex={0}
      aria-label={`${species.name} — ${translateCategory(species.plant_category || species.plant_type)} , obtížnost ${getDifficultyLabel(difficulty)}`}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }}
      className={cn('cursor-pointer focus-visible:outline-2 focus-visible:outline-green-500 focus-visible:outline-offset-2 rounded-2xl', className)}
    >
      <Card
        variant="glass"
        padding="none"
        className="h-full hover:border-green-500/30 transition-colors overflow-hidden group relative flex flex-col"
      >
        {/* Background image (if icon available) */}
        {species.icon ? (
          <div className="absolute inset-0 z-0">
            <img
              src={species.icon}
              alt=""
              className={cn(
                'w-full h-full object-cover opacity-25 filter blur-sm grayscale',
                !reducedMotion && 'group-hover:opacity-35 group-hover:scale-105 group-hover:grayscale-0 transition-all duration-700',
              )}
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-transparent dark:from-zinc-900 dark:via-zinc-900/80 dark:to-zinc-900/30" />
          </div>
        ) : (
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 to-green-600" />
        )}

        {/* Content */}
        <div className="p-6 relative z-10 flex flex-col h-full">
          {/* Header: Icon + Category */}
          <div className="flex justify-between items-start mb-2">
            <div className="relative">
              {species.icon ? (
                <div className="relative w-12 h-12">
                  <img
                    src={species.icon}
                    alt={species.name}
                    className="w-12 h-12 rounded-xl object-cover shadow-md border-2 border-white dark:border-zinc-700"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      if (target.nextElementSibling) {
                        (target.nextElementSibling as HTMLElement).style.display = 'flex';
                      }
                    }}
                  />
                  <div className="hidden absolute inset-0 items-center justify-center p-3 bg-green-100 dark:bg-green-900/30 rounded-xl text-green-600 dark:text-green-400">
                    <Leaf size={24} />
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl text-green-600 dark:text-green-400">
                  <Leaf size={24} />
                </div>
              )}
            </div>
            <span className="text-xs px-2 py-1 rounded-full bg-white/80 dark:bg-black/50 backdrop-blur-sm text-gray-700 dark:text-zinc-300 font-medium capitalize border border-white/20">
              {translateCategory(species.plant_category || species.plant_type)}
            </span>
          </div>

          {/* Name */}
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1 group-hover:text-green-600 transition-colors">
            {species.name}
          </h3>
          {species.scientific_name && (
            <p className="text-sm text-gray-500 italic mb-3">{species.scientific_name}</p>
          )}

          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-zinc-300 line-clamp-3 mb-4 flex-grow">
            {species.description || 'Bez popisu.'}
          </p>

          {/* Quick Stats — explicit labels + dot ratings */}
          <div className="space-y-2 mb-4 pt-3 border-t border-gray-100 dark:border-zinc-800/50">
            {/* Difficulty */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-zinc-400">
                <Puzzle size={13} className={getDifficultyColor(difficulty).replace('bg-', 'text-')} />
                <span className="font-medium">Obtížnost</span>
              </div>
              <div className="flex items-center gap-2">
                <DotRating value={difficulty} activeColor={getDifficultyColor(difficulty)} label="Obtížnost" />
                <span className="text-gray-500 dark:text-zinc-400 min-w-[60px] text-right">
                  {DIFFICULTY_LABELS[difficulty] || `${difficulty}/5`}
                </span>
              </div>
            </div>

            {/* Water */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-zinc-400">
                <Droplets size={13} className="text-cyan-500" />
                <span className="font-medium">Zálivka</span>
              </div>
              <div className="flex items-center gap-2">
                {waterNeed ? (
                  <>
                    <DotRating value={waterNeed} activeColor="bg-cyan-500" label="Zálivka" />
                    <span className="text-gray-500 dark:text-zinc-400 min-w-[60px] text-right">
                      {WATER_LABELS[waterNeed] || `${waterNeed}/5`}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400 italic">Neuvedeno</span>
                )}
              </div>
            </div>

            {/* Nutrients */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1.5 text-gray-600 dark:text-zinc-400">
                <FlaskConical size={13} className="text-teal-500" />
                <span className="font-medium">Živiny</span>
              </div>
              <div className="flex items-center gap-2">
                {nutrientDemand ? (
                  <>
                    <DotRating value={nutrientDemand} activeColor="bg-teal-500" label="Živiny" />
                    <span className="text-gray-500 dark:text-zinc-400 min-w-[60px] text-right">
                      {NUTRIENT_LABELS[nutrientDemand] || `${nutrientDemand}/5`}
                    </span>
                  </>
                ) : (
                  <span className="text-gray-400 italic">Neuvedeno</span>
                )}
              </div>
            </div>

            {/* Sowing Time */}
            {hasSowing && (
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1.5 text-gray-600 dark:text-zinc-400">
                  <Calendar size={13} className="text-orange-500" />
                  <span className="font-medium">Výsev</span>
                </div>
                <div className="text-xs font-semibold text-orange-600 dark:text-orange-400 text-right">
                  {sowingIndoor || sowingOutdoor ? (
                    <div className="flex flex-col items-end gap-0.5">
                      {sowingIndoor && (
                        <span>Doma: {formatSowingTime(sowingIndoor)}</span>
                      )}
                      {sowingOutdoor && (
                        <span>Venku: {formatSowingTime(sowingOutdoor)}</span>
                      )}
                    </div>
                  ) : (
                    <span>{formatSowingTime(sowingGeneral)}</span>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Companions Preview */}
          {((species.good_companions?.length ?? 0) > 0 ||
            (species.bad_companions?.length ?? 0) > 0) && (
            <div className="flex flex-col gap-1.5 mb-3 text-xs text-gray-500">
              {species.good_companions && species.good_companions.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Users size={12} className="text-green-500 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-1">
                    <span className="text-green-600 dark:text-green-400 font-medium">Dobří sousedé:</span>{' '}
                    {species.good_companions.join(', ')}
                  </span>
                </div>
              )}
              {species.bad_companions && species.bad_companions.length > 0 && (
                <div className="flex items-start gap-1.5">
                  <Users size={12} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-1">
                    <span className="text-red-600 dark:text-red-400 font-medium">Špatní sousedé:</span>{' '}
                    {species.bad_companions.join(', ')}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center text-green-600 text-sm font-medium group-hover:translate-x-1 transition-transform mt-auto pt-2 border-t border-gray-100 dark:border-zinc-800/50">
            Detail druhu <ArrowRight size={16} className="ml-1" />
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default SpeciesCard;
